import { ALL_SUPPORTED_CONTENT_TYPES } from '@client/utils/constants';
import { logError } from '@client/utils/log';
import { fetchJSON } from '@client/utils/rest';
import { warn } from '@client/utils/toast';
const alexandriaSearchURL = process.env.ALEXANDRIA_SEARCH_URL || 'https://alexandria-search.newsela.com';

const defaultSort = [
  {
    updated_at: {
      order: 'desc'
    }
  }
];

/**
 * Builds the OpenSearch query.
 *
 * @param {Object} params - The parameters for building the query.
 * @param {string} params.query - The search query string.
 * @param {boolean} params.status - Status of the content we want to query.
 * @param {Object} params.config - The configuration object.
 * @param {string} params.config.index - The index to query.
 * @param {Array<string>} params.config.fields - The fields to search.
 * @param {Array<Object>} params.config.sort - The sort configuration.
 * @param {Array<string>} params.config._source - The fields to return in the source.
 * @param {boolean} params.config.disableSortOnQuery - Flag to disable sorting when a query is present.
 * @param {number} params.pageSize - The number of results per page.
 * @param {number} params.currentPage - The current page number.
 * @param {Array} params.status - Status of the content.
 * @param {boolean} params.includeChildBundles - If we should search in child bundles too.
 *
 * @returns {Object} The OpenSearch query object.
 *
 * @throws Will throw an error if index, fields, or _source are not provided in the config.
 */
// TODO: Remove the isArchived param
function buildQuery ({ query, config, pageSize, currentPage, status, includeChildBundles, includeContentProvider, includeContentTypes, articleType, isArchived }) {
  const sort = (config.disableSortOnQuery && query) ? [] : (config.sort || defaultSort);

  const mustClauses = [
    {
      simple_query_string: {
        query: query || '*',
        fields: config.fields
      }
    }
  ];

  if (config.index === 'bundle') {
    if (includeChildBundles) {
      mustClauses.push({
        range: {
          parents_count: {
            gte: 0
          }
        }
      });
    } else {
      mustClauses.push({
        term: {
          parents_count: 0
        }
      });
    }
  }

  if (config.index === 'article') {
    if (includeContentProvider && includeContentProvider?.name !== 'All Providers' && includeContentProvider?.name !== null) {
      mustClauses.push({
        term: {
          'content_provider.name.keyword': includeContentProvider.name
        }
      });
    }
    if (articleType && articleType.length > 0) {
      mustClauses.push({
        terms: {
          'article_type.keyword': articleType
        }
      });
    }
  }

  // There is no actual index in our OS clusters that is called inventory.
  // The index name was set to inventory in client/common/containers/schema/content on purpose,for the following reasons:
  // 1. Inventory management is used to search multiple content types, so the index changes based on the content filter selection.
  // 2. This allows us to detect when the user is initiating requests from Inventory management page.
  // 3. This allows us to dynamically select the indices based on the selected content types.
  const inventoryChosenIndices =
    config.index === 'inventory'
      ? (includeContentTypes.length === 0
          ? ALL_SUPPORTED_CONTENT_TYPES
          : includeContentTypes)
          .map((c) => c.toLowerCase())
          .join(',')
      : '';

  if (config.index !== 'content_provider') {
    if (isArchived) {
      mustClauses.push({
        terms: {
          status: ['published', 'draft', 'changed', 'archived']
        }
      });
    } else if (status) {
      mustClauses.push({
        terms: {
          status: status.length === 0 ? ['published', 'draft', 'changed'] : status
        }
      });
    }
  }

  return {
    indices: config.index === 'inventory' ? inventoryChosenIndices : config.index,
    query: {
      query: {
        bool: {
          must: mustClauses
        }
      },
      _source: config._source,
      size: pageSize,
      from: currentPage * pageSize,
      sort
    }
  };
}

/**
 * Constructs a personalized (query for upper tables of /articles & /bundles) OpenSearch query based on the provided parameters.
 *
 * @param {Object} params - The parameters for building the query.
 * @param {Object} params.config - The configuration object.
 * @param {string} params.config.index - The index to search in.
 * @param {Array<string>} [params.config._source] - The fields to include in the response.
 * @param {number} params.pageSize - The number of documents per page.
 * @param {number} params.currentPage - The current page number.
 * @param {number} params.userId - The user ID to filter documents by.
 * @returns {Object} The constructed OpenSearch query.
 */
function buildPersonalizedQuery ({ config, pageSize, currentPage, userId }) {
  const mustClauses = [
    {
      term: {
        'updated_by.id': userId
      }
    },
    {
      terms: {
        status: ['published', 'draft', 'changed']
      }
    }
  ];

  if (config.index === 'bundle') {
    mustClauses.push({
      term: {
        parents_count: 0
      }
    });
  }

  return {
    indices: config.index,
    query: {
      query: {
        bool: {
          must: mustClauses
        }
      },
      _source: config._source,
      size: pageSize,
      from: currentPage * pageSize,
      sort: defaultSort
    }
  };
}

/**
 * Builds an OpenSearch query to search for a specific content ID.
 *  @param {Object} params.config - The configuration object.
 *  @param {cuid} params.contentId - The content id of an Alexandria content.
 * @returns {Object} The OpensSearch query object.
 */
function buildExactContentIdQuery ({ config, contentId }) {
  const mustClauses = [
    {
      match: {
        id: contentId
      }
    }
  ];

  return {
    indices: config.index === 'inventory' ? ALL_SUPPORTED_CONTENT_TYPES.map((c) => c.toLowerCase()).join(',') : config.index,
    query: {
      query: {
        bool: {
          must: mustClauses
        }
      },
      _source: config._source,
    }
  };
}

/**
 * Fetches data from the specified OpenSearch indices.
 *
 * @param {Object} params - The parameters for fetching data.
 * @param {string} params.query - The search query string.
 * @param {number} params.pageSize - The number of results per page.
 * @param {number} params.currentPage - The current page number.
 * @param {Object} params.config - The configuration object for the query.
 * @param {Array} params.status - Status of the content.
 * @param {boolean} params.includeChildBundles - If we should search in child bundles too.
 *
 * @returns {Object} The results of the OpenSearch query.
 */
// TODO: Remove the isArchived param, should be only used temporarily till final designs are ready.
export async function fetchFromOpenSearch ({ query, pageSize = 10, currentPage = 0, config, status = [], includeChildBundles = false, includeContentProvider = null, includeContentTypes = [], articleType = [], id = null, isArchived = false }) {
  try {
    validateQueryFields(config);

    // First, check if the user pasted an Alexandria URL.
    const contentId = detectUrlAndExtractId(query);

    let staticQuery;
    if (contentId) {
      // The user pasted an Alexandria URL.
      staticQuery = buildExactContentIdQuery({ config, contentId });
    } else if (id) {
      // This is not a user query but the query to get the contents that the usr has edited.
      staticQuery = buildPersonalizedQuery({ config, pageSize, currentPage, userId: id });
    } else {
      staticQuery = buildQuery({ query, config, pageSize, currentPage, status, includeChildBundles, includeContentProvider, includeContentTypes, articleType, isArchived });
    }
    const response = await fetchJSON(`${alexandriaSearchURL}/api/elastic/query/`, {
      method: 'POST',
      body: JSON.stringify(staticQuery),
      headers: {
        'Content-Type': 'application/json',
      }
    });

    let hits = response.body.hits.hits.map((hit) => hit._source);
    // Apply mapping to the response hits if mapping is provided
    if (config.mapping) {
      hits = hits.map((hit) => applyMapping(hit, config.mapping));
    }

    hits = appendEmptyObjectForPagination(hits, pageSize, currentPage);

    return { results: { contents: hits, totalContents: response.body.hits.total.value }, error: null };
  } catch (error) {
    logError('Error fetching data:', error);
    warn('There was an error fetching the data', { persist: true });
    return { results: { contents: [], totalContents: 0 }, error };
  }
}

/**
 * Detects if the input query is a URL and extracts the article, bundle, or inventory ID if applicable.
 *
 * @param {string} query - The input query string which could be a URL or plain text.
 * @returns {string|null} The extracted article, bundle, or inventory ID if the query is a URL, otherwise null.
 */
function detectUrlAndExtractId (query) {
  if (!query) {
    return null;
  }

  // Regular expression to match the article, bundle, or inventory URL and extract the ID.
  // Updated to support both old and new domain formats with optional .app subdomain
  const urlPattern = /https:\/\/alexandria\.(?:app\.)?(dev\.newseladev|staging\.newseladev|newsela)\.com\/(articles|bundles|inventory)\/([a-zA-Z0-9]+)/;
  const match = query.match(urlPattern);

  if (match) {
    // If the query is a URL, extract and return the ID.
    return match[3];
  }

  // If the query is not a URL, return null.
  return null;
}

/**
 * Applies a key mapping to an object or array of objects.
 * Used to adjust the case from openSearch to what is used in Alexandria.
 * Exported for testing.
 *
 * This function takes an object or array of objects and transforms its keys based on the provided
 * mapping. If the mapping specifies a transformation for a key, the key is replaced with the
 * corresponding mapped key. The function is recursive and will apply the mapping to nested objects
 * and arrays as well.
 *
 * @param {Object|Array} obj - The object or array of objects to which the mapping should be applied.
 * @param {Object} mapping - The mapping configuration object. The keys of this object are the
 *                           desired key names, and the values are the original key names.
 * @returns {Object|Array} - A new object or array of objects with keys transformed according to the mapping.
 */
export function applyMapping (obj, mapping) {
  // If the current object is an array
  if (Array.isArray(obj)) {
    // Apply the mapping function to each element in the array
    return obj.map((item) => applyMapping(item, mapping));
  } else if (obj !== null && typeof obj === 'object') {
    // If the current object is a non-null object
    // Create a new object to append the mapped keys and values
    return Object.keys(obj).reduce((acc, key) => {
      // Get the new key from the mapping object or use the original key if not found
      const mappedKey = Object.keys(mapping).find((mappedKey) => mapping[mappedKey] === key) || key;
      // Call the function  again with the new key.
      acc[mappedKey] = applyMapping(obj[key], mapping);
      return acc;
    }, {});
  }
  // If the object is neither an array nor an object, return it as is.
  return obj;
}

/**
 * Appends empty objects to the beginning of the hits array for pagination purposes.
 *
 * This function is designed to be mimic Apollo's `refetch()` function.
 * When `refetch()` is called, new data is appended to the existing data. This function mimics
 * that behavior by prepending a specific number of empty objects to the `hits` array when
 * a search request is sent to OpenSearch, because we use the same pagination function.
 *
 * @param {Array<Object>} hits - The array of hits (results) to which empty objects should be prepended.
 * @param {number} pageSize - The number of hits per page.
 * @param {number} currentPage - The current page number.
 * @returns {Array<Object>} - A new array with empty objects prepended.
 */
function appendEmptyObjectForPagination (hits, pageSize, currentPage) {
  if (currentPage !== 0) {
    // Calculate the number of empty objects needed
    const numberOfEmptyObjects = currentPage * pageSize;

    // Create an array of empty objects
    const emptyObjectsArray = Array.from({ length: numberOfEmptyObjects }, () => ({}));

    // Add the empty objects at the beginning of the list of hits
    return [...emptyObjectsArray, ...hits];
  }
  return hits;
}

/**
 * Validates the configuration fields for the openSearch query.
 *
 * @param {Object} config - The configuration object.
 * @param {string} config.index - The index to search in.
 * @param {Array<string>} config.fields - The fields to query.
 * @param {Array<string>} config._source - The fields to include in the response.
 * @throws {Error} Throws an error if required fields are missing or invalid.
 */
function validateQueryFields (config) {
  const error = [];
  if (!config.index) {
    error.push('Index is required in the config object');
  }
  if (!config.fields || !Array.isArray(config.fields) || config.fields.length === 0) {
    error.push('Fields are required in the config object');
  }
  if (!config._source || !Array.isArray(config._source) || config._source.length === 0) {
    error.push('_source is required in the config object');
  }

  if (error.length !== 0) {
    logError("configuration error, can't request data", error);
    throw new Error(error.join(', '));
  }
}
